/**
 * This the main document viewer component for StanfordTax used to view, annotate, and manipulate PDF documents.
 *
 * It comes with a toolbar for similar to what you would get in a typical PDF viewer. For example:
 * - Zoom in/out
 * - Text selection
 * - Text, rectangle, and stamp annotations
 * - Commenting with a comment sidebar
 *
 * It also has some tax specific features such as:
 * - Split bundled document
 * - Mark as prepared and reviewed
 */
import { match, P } from '@st/pattern'
import {
  type DocumentViewerMessage,
  DocumentViewport,
  Layer,
  Page,
  PDFPageSurface,
  resolvePresentedPageStructure,
  selPageRenderStateByPageNumber,
  selViewerPageTransform,
  selViewerZoomLevel
} from '@st/document-viewer-kit'
import {
  AnnotationLocalMessage,
  selActiveAnnotation,
  selAnnotationsPluginState,
  toAnnotationsPluginMessage,
  toViewerMessageFromAnnotationToolMessage,
  ToolMessage
} from '@st/document-viewer-kit/plugins/annotations'
import { DocumentAnnotation, DocumentPermission, PageGroup, STDocument } from '@st/sdk'
import {
  AIIcon,
  Button,
  CalculatorIcon,
  CheckIcon,
  CommentIcon,
  CursorIcon,
  Dialog,
  DialogButtons,
  LinkIcon,
  Modal,
  RectangleIcon,
  RotateCCWIcon,
  RotateCWIcon,
  StampIcon,
  TextIcon,
  ToggleButton,
  Toolbar,
  ToolbarSection,
  ToolbarSectionDivider,
  ZoomInIcon,
  ZoomOutIcon
} from '@st/theme'
import { clsx } from 'clsx'
import { ReactNode, useMemo, useState } from 'react'
import { useSendViewerMessage } from '~/features/st-folder-viewer/st-folder-hooks'
import { STAnnotationsProvider } from '~/features/st-folder-viewer/st-annotations-provider'
import type { STFolderMessage } from '~/features/st-folder-viewer/st-folder-module'
import {
  selFolderState,
  selSidebarVisibility,
  selFieldsSidebarShown
} from '~/features/st-folder-viewer/st-folder-selectors'
import { useSelect } from '~/store'
import { ToolState } from './annotations'
import { useResolvePageText } from './page-text-boundary'
import { ViewerPaginationChipOverlay } from './floating-page-chip'
import { PageGroupDivider } from './page-groups/page-group-divider'
import { commentsSidebarPanel } from './plugins/comment/sidebar'
import {
  type SidebarVisibility,
  ViewerSidebarFrame,
  type ViewerSidebarPanel,
  ViewerSidebarToggle
} from './sidebar-layout'
import { FieldsSidebar } from './plugins/field/ui'
import { RectangleToolEditor } from './plugins/rectangle'
import { StampToolEditor } from './plugins/stamp'
import { CheckmarkToolEditor } from './plugins/checkmark'
import { CrossRefToolEditor } from './plugins/cross-references'
import { TextToolEditor } from './plugins/text'
import { STDocumentPageToolbar } from './st-document-page-toolbar'
import { PDF_TOOLBAR_FEATURES, type ToolbarFeature } from './toolbar-features'
import { AnnotationsOverlay, ST_DOC_VIEWER_ZOOM_LEVELS } from './runtime-definition'
import {
  selRightSidebarMode,
  selViewerAnnotationsByPageNumber,
  type STViewerAnnotation
} from './viewer-annotations'
import { FEATURE_FLAGS } from '~/env'
import type { JSONSchema } from './schema-document-data'
import type { STPDFDocumentViewerOptions } from './document-viewer-options'
export type { STPDFDocumentViewerOptions } from './document-viewer-options'

type Props = {
  doc: STDocument
  folderId: string
  isArchived: boolean
  pageGroups: PageGroup[]
  viewerOptions: STPDFDocumentViewerOptions
  renderTopBar: (sendViewerMessage: (message: DocumentViewerMessage) => void) => ReactNode
  renderSidebar: (
    sendViewerMessage: (message: DocumentViewerMessage) => void,
    fallback: ViewerSidebarPanel | null
  ) => ViewerSidebarPanel | null
  send: (message: STFolderMessage) => void
  onDeletePage: (pageNumber: number) => void
  onRestorePage: (pageNumber: number) => void
  onAddPageGroupBoundary: (afterPageNumber: number) => void
  onRemovePageGroupBoundary: (afterPageNumber: number) => void
  onRotatePage: (pageNumber: number, rotation: number) => void
  onRotateDocument: (delta: 90 | -90) => void
}
export function STPDFDocViewer({
  doc,
  folderId,
  isArchived,
  viewerOptions,
  pageGroups,
  renderTopBar,
  renderSidebar,
  send,
  onDeletePage,
  onRestorePage,
  onAddPageGroupBoundary,
  onRemovePageGroupBoundary,
  onRotatePage,
  onRotateDocument
}: Props) {
  const { readOnly, pageGroupMode } = viewerOptions
  const sendViewerMessage = useSendViewerMessage(send)

  const { documentPermissions, toolbarFeatures } = match(readOnly)
    .with(true, () => ({
      documentPermissions: doc.permissions.filter(
        (permission) =>
          permission != 'delete_pages' && permission != 'rotate_pages' && permission != 'split'
      ),
      toolbarFeatures: ['zoom'] as const
    }))
    .with(false, () => ({
      documentPermissions: doc.permissions,
      toolbarFeatures: PDF_TOOLBAR_FEATURES
    }))
    .exhaustive()

  return (
    <STAnnotationsProvider>
      <STNewPDFDocViewerInner
        doc={doc}
        folderId={folderId}
        isArchived={isArchived}
        pageGroupMode={pageGroupMode}
        readOnly={readOnly}
        annotationToolsEnabled={viewerOptions.annotationToolsEnabled}
        topBar={renderTopBar(sendViewerMessage)}
        renderSidebar={renderSidebar}
        sendViewerMessage={sendViewerMessage}
        send={send}
        toolbarFeatures={toolbarFeatures}
        documentPermissions={documentPermissions}
        pageGroups={pageGroups}
        onDeletePage={onDeletePage}
        onRestorePage={onRestorePage}
        onAddPageGroupBoundary={onAddPageGroupBoundary}
        onRemovePageGroupBoundary={onRemovePageGroupBoundary}
        onRotatePage={onRotatePage}
        onRotateDocument={onRotateDocument}
      />
    </STAnnotationsProvider>
  )
}

function STNewPDFDocViewerInner({
  doc,
  folderId,
  isArchived,
  pageGroupMode,
  readOnly,
  annotationToolsEnabled,
  topBar,
  renderSidebar,
  sendViewerMessage,
  send,
  toolbarFeatures,
  documentPermissions,
  pageGroups,
  onDeletePage,
  onRestorePage,
  onAddPageGroupBoundary,
  onRemovePageGroupBoundary,
  onRotatePage,
  onRotateDocument
}: {
  doc: STDocument
  folderId: string
  isArchived: boolean
  pageGroupMode: STPDFDocumentViewerOptions['pageGroupMode']
  readOnly: boolean
  annotationToolsEnabled: boolean
  topBar: ReactNode
  renderSidebar: (
    sendViewerMessage: (message: DocumentViewerMessage) => void,
    fallback: ViewerSidebarPanel | null
  ) => ViewerSidebarPanel | null
  sendViewerMessage: (message: DocumentViewerMessage) => void
  send: (message: STFolderMessage) => void
  toolbarFeatures: readonly ToolbarFeature[]
  documentPermissions: DocumentPermission[]
  pageGroups: PageGroup[]
  onDeletePage: (pageNumber: number) => void
  onRestorePage: (pageNumber: number) => void
  onAddPageGroupBoundary: (afterPageNumber: number) => void
  onRemovePageGroupBoundary: (afterPageNumber: number) => void
  onRotatePage: (pageNumber: number, rotation: number) => void
  onRotateDocument: (delta: 90 | -90) => void
}) {
  const resolvePageText = useResolvePageText()
  const viewportStatus = useSelect('stFolder', (state) => state.viewer.pagesStatus)
  const viewportPages = useSelect('stFolder', (state) => state.viewer.pages)
  const viewportState = useSelect('stFolder', (state) => state.viewer.viewportState)
  const pageRenderStateByPageNumber = useSelect(
    'stFolder',
    (state) => state.viewer.pageRenderStateByPageNumber
  )
  const showViewportPages = useSelect(
    'stFolder',
    (state) => state.viewer.viewer.viewportMeasurement != null
  )
  const pageTransform = useSelect('stFolder', (state) =>
    selViewerPageTransform(state.viewer.viewer)
  )
  const annotationState = useSelect('stFolder', (state) => selAnnotationsPluginState(state.viewer))
  const activeAnnotation = useSelect(
    'stFolder',
    (state) => selActiveAnnotation(state.viewer) as STViewerAnnotation | undefined
  )
  const rightSidebarMode = useSelect('stFolder', (state) => selRightSidebarMode(state.viewer))
  const sidebarVisibility = useSelect('stFolder', selSidebarVisibility)
  const annotationsByPageNumber = useSelect('stFolder', (state) =>
    selViewerAnnotationsByPageNumber(state.viewer)
  )
  const folderState = useSelect('stFolder', selFolderState)
  const folder = folderState?.folder
  const taxReturnUnits = folderState?.taxReturnUnits ?? []
  const dataSchemas = folderState?.dataSchemas ?? {}
  const dataSchema = match(doc.documentTypeId)
    .with(P.string, (documentTypeId) => dataSchemas[documentTypeId] as JSONSchema | undefined)
    .with(P.nullish, () => undefined)
    .exhaustive()
  const [dialog, setDialog] = useState<{ type: 'splitUnavailable' } | undefined>()
  const handleAddPageGroupBoundary: typeof onAddPageGroupBoundary | undefined = match(pageGroupMode)
    .with('enabled', () => onAddPageGroupBoundary)
    .with('hint', () => () => setDialog({ type: 'splitUnavailable' }))
    .with('hidden', () => undefined)
    .exhaustive()
  const canRefreshTaxReturn = Boolean(
    !readOnly && FEATURE_FLAGS.pushTaxReturn && folder?.taxReturnId && !isArchived
  )
  const handleRefreshTaxReturn = match(canRefreshTaxReturn)
    .with(true, () => () => send({ type: 'refreshTaxReturn' as const }))
    .with(false, () => undefined)
    .exhaustive()
  const showFieldsSidebar =
    !readOnly &&
    FEATURE_FLAGS.pushTaxReturn &&
    dataSchema != null &&
    doc.documentTypeId != 'tax_return_packet'
  const fieldsSidebarShown = useSelect('stFolder', selFieldsSidebarShown)
  const effectiveRightSidebarMode = match(
    FEATURE_FLAGS.pushTaxReturn && annotationState.selectedToolName == 'field' && showFieldsSidebar
  )
    .with(true, () => 'fields' as const)
    .with(false, () => rightSidebarMode)
    .exhaustive()
  const deletedPageNumbers = doc.deletedPageNumbers ?? []
  const pageRotations = doc.pageRotations
  const defaultSidebar = match(effectiveRightSidebarMode)
    .returnType<ViewerSidebarPanel | null>()
    .with('comments', () => commentsSidebarPanel(sendViewerMessage))
    .with('fields', () =>
      match(dataSchema)
        .with(undefined, () => null)
        .otherwise((dataSchema) => ({
          title: 'Extracted fields',
          content: (
            <FieldsSidebar
              sendViewerMessage={sendViewerMessage}
              pages={viewportPages}
              onRefreshTaxReturn={handleRefreshTaxReturn}
              documentTypeId={doc.documentTypeId}
              taxReturnUnitPath={doc.taxReturnUnitPath}
              documentData={doc.data}
              dataSchema={dataSchema}
              taxReturnUnits={taxReturnUnits}
              onLinkTaxReturnUnit={(taxReturnUnitPath) =>
                send({
                  type: 'linkDocumentTaxReturnUnit',
                  folderId,
                  documentId: doc.id,
                  documentName: doc.name,
                  taxReturnUnitPath
                })
              }
            />
          )
        }))
    )
    .with('none', () => null)
    .exhaustive()
  const sidebar = renderSidebar(sendViewerMessage, defaultSidebar)

  return (
    <DocViewerScaffold
      toolbar={
        <PDFDocToolbar
          sendViewerMessage={sendViewerMessage}
          onRotateDocument={onRotateDocument}
          features={toolbarFeatures}
          fieldsSidebar={match(showFieldsSidebar)
            .with(true, () => ({
              shown: fieldsSidebarShown,
              onToggle: () => send({ type: 'fieldsSidebarToggled' })
            }))
            .with(false, () => undefined)
            .exhaustive()}
        />
      }
      banner={topBar}
      sidebar={match(sidebar)
        .with(null, () => undefined)
        .otherwise((panel) => ({
          panel,
          visibility: sidebarVisibility,
          onVisibilityChange: (visibility) => send({ type: 'sidebarVisibilityChanged', visibility })
        }))}
    >
      <DocumentViewport
        send={sendViewerMessage}
        status={viewportStatus}
        showPages={showViewportPages}
        pagesToRender={viewportState.pagesToRender}
        pageTransform={pageTransform}
        ariaLabel={
          viewportStatus == 'loading'
            ? `Loading document ${doc.name}`
            : `Viewing document ${doc.name}`
        }
        loadingStatusLabel={`Loading document ${doc.name}`}
      >
        {viewportPages.map((pageStructure, index) => {
          const nextPageStructure = viewportPages[index + 1]
          const pageRenderState = selPageRenderStateByPageNumber(
            pageRenderStateByPageNumber,
            pageStructure.pageNumber
          )
          const presentedPageStructure = resolvePresentedPageStructure(
            pageStructure,
            pageRenderState,
            resolvePageText
          )
          const topDivider =
            pageStructure.pageNumber == 1 && handleAddPageGroupBoundary ? (
              <PageGroupDivider
                pageBelow={pageStructure}
                pageGroups={pageGroups}
                onAddPageGroupBoundary={handleAddPageGroupBoundary}
                onRemovePageGroupBoundary={onRemovePageGroupBoundary}
              />
            ) : undefined

          const bottomDivider = handleAddPageGroupBoundary ? (
            <PageGroupDivider
              pageAbove={pageStructure}
              pageBelow={nextPageStructure}
              pageGroups={pageGroups}
              onAddPageGroupBoundary={handleAddPageGroupBoundary}
              onRemovePageGroupBoundary={onRemovePageGroupBoundary}
            />
          ) : null

          return (
            <Page
              key={pageStructure.pageNumber}
              page={pageStructure}
              pageToolbar={
                <STDocumentPageToolbar
                  page={pageStructure}
                  permissions={documentPermissions}
                  isDeleted={deletedPageNumbers.includes(pageStructure.pageNumber)}
                  rotation={pageRotations[pageStructure.pageNumber] || 0}
                  onDeletePage={onDeletePage}
                  onRestorePage={onRestorePage}
                  onRotatePage={onRotatePage}
                />
              }
              topDivider={topDivider}
              bottomDivider={bottomDivider}
            >
              <Layer>
                <PDFPageSurface send={sendViewerMessage} pageStructure={pageStructure} />
              </Layer>

              <Layer>
                <AnnotationsOverlay
                  send={sendViewerMessage}
                  page={presentedPageStructure}
                  annotations={annotationsByPageNumber[pageStructure.pageNumber] ?? []}
                  annotationState={annotationState}
                  activeAnnotation={activeAnnotation}
                  renderToolOverlay={annotationToolsEnabled}
                />
              </Layer>
            </Page>
          )
        })}
      </DocumentViewport>

      <Modal isOpen={dialog != undefined}>
        {match(dialog)
          .with(undefined, () => null)
          .with({ type: 'splitUnavailable' }, () => (
            <SplitUnavailableDialog onClose={() => setDialog(undefined)} />
          ))
          .exhaustive()}
      </Modal>
    </DocViewerScaffold>
  )
}

function SplitUnavailableDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog
      title="Move document to split it"
      subtitle="To split this PDF, move it to the Bundled Document category first. Once it is in that category, you can click between pages to split it."
      buttons={
        <DialogButtons>
          <Button variant="primary" onClick={onClose}>
            OK
          </Button>
        </DialogButtons>
      }
    />
  )
}

/**
 * Lays out a document viewer as two columns. The document column stacks the
 * optional toolbar and banner above the scrollable document; the sidebar is a
 * full-height column beside it, so its header shares the toolbar's row and a
 * document banner never spans it.
 *
 * The scaffold owns visibility for every sidebar kind. Collapsing removes the
 * sidebar column, so the document column takes the full width, and moves the
 * same toggle to the end of the toolbar row. A collapsed sidebar is unmounted:
 * any state a panel must keep belongs in the model.
 */
export function DocViewerScaffold({
  toolbar,
  banner,
  children,
  sidebar
}: {
  toolbar?: ReactNode
  banner?: ReactNode
  children: ReactNode
  sidebar?: {
    panel: ViewerSidebarPanel
    visibility: SidebarVisibility
    onVisibilityChange: (visibility: SidebarVisibility) => void
  }
}) {
  const sidebarColumn = match(sidebar)
    .with({ visibility: 'expanded' }, ({ panel, onVisibilityChange }) => (
      <ViewerSidebarFrame panel={panel} onHide={() => onVisibilityChange('collapsed')} />
    ))
    .otherwise(() => undefined)
  const toolbarRow = match(sidebar)
    .with({ visibility: 'collapsed' }, ({ onVisibilityChange }) => (
      <div className="grid grid-cols-[minmax(0,1fr)_auto]">
        {toolbar ?? <Toolbar />}
        <div className="flex items-center border-b border-neutral-200 bg-neutral-100 pr-5">
          <ViewerSidebarToggle expanded={false} onToggle={() => onVisibilityChange('expanded')} />
        </div>
      </div>
    ))
    .otherwise(() => toolbar)

  return (
    <div
      className={clsx('grid h-full min-h-0 overflow-hidden', {
        'grid-cols-[minmax(0,1fr)_auto]': sidebarColumn,
        'grid-cols-[1fr]': !sidebarColumn
      })}
    >
      <div
        className={clsx('grid min-h-0 overflow-hidden', {
          'grid-rows-[50px_56px_auto]': toolbarRow && banner,
          'grid-rows-[50px_auto]': toolbarRow && !banner,
          'grid-rows-[56px_auto]': !toolbarRow && banner,
          'grid-rows-[auto]': !toolbarRow && !banner
        })}
      >
        {toolbarRow}
        {banner}
        <div className="relative h-full w-full overflow-hidden">
          {children}
          <ViewerPaginationChipOverlay />
        </div>
      </div>
      {sidebarColumn}
    </div>
  )
}

export function PDFDocToolbar({
  sendViewerMessage,
  onRotateDocument,
  features,
  fieldsSidebar
}: {
  sendViewerMessage: (message: DocumentViewerMessage) => void
  onRotateDocument?: (delta: 90 | -90) => void
  features: readonly ToolbarFeature[]
  /** Extracted-fields toggle; `shown` is whether the fields panel is visible. */
  fieldsSidebar?: { shown: boolean; onToggle: () => void }
}) {
  const zoomLevel = useSelect('stFolder', (state) => selViewerZoomLevel(state.viewer.viewer))
  const sendAnnotationMessage = useMemo(
    () => (message: AnnotationLocalMessage) => {
      sendViewerMessage(toAnnotationsPluginMessage(message))
    },
    [sendViewerMessage]
  )
  const sendAnnotationToolMessage = useMemo(
    () => (message: ToolMessage<DocumentAnnotation, ToolState>) => {
      sendViewerMessage(toViewerMessageFromAnnotationToolMessage(message))
    },
    [sendViewerMessage]
  )
  const annotationState = useSelect('stFolder', (state) => selAnnotationsPluginState(state.viewer))
  const selectedToolName = annotationState.selectedToolName
  const tool = annotationState.toolStates[selectedToolName] as ToolState | undefined
  const activeAnnotation = useSelect(
    'stFolder',
    (state) => selActiveAnnotation(state.viewer) as STViewerAnnotation | undefined
  )
  const showZoomControls = features.includes('zoom')
  const showRotateControls = features.includes('rotate')
  const showAnnotationControls = features.includes('annotate')
  const showSelectTool = features.includes('select')
  const showCaretTool = features.includes('caret')
  const showDividerAfterZoom = showZoomControls && (showRotateControls || showAnnotationControls)
  const showDividerAfterRotate = showRotateControls && showAnnotationControls

  return (
    <Toolbar className="gap-4">
      {showZoomControls && (
        <ToolbarSection>
          <Button
            variant="subtle"
            disabled={resolveZoomLevel(zoomLevel, -1) === undefined}
            onClick={() =>
              sendViewerMessage({
                type: 'setViewerZoom',
                level: resolveZoomLevel(zoomLevel, -1)!
              })
            }
            aria-label="Zoom out"
          >
            <ZoomOutIcon className="size-6" />
          </Button>
          <Button
            variant="subtle"
            disabled={resolveZoomLevel(zoomLevel, 1) === undefined}
            onClick={() =>
              sendViewerMessage({
                type: 'setViewerZoom',
                level: resolveZoomLevel(zoomLevel, 1)!
              })
            }
            aria-label="Zoom in"
          >
            <ZoomInIcon className="size-6" />
          </Button>
        </ToolbarSection>
      )}

      {showDividerAfterZoom && <ToolbarSectionDivider />}

      {showRotateControls && (
        <ToolbarSection className="gap-1">
          <Button
            variant="subtle"
            className="px-1"
            onClick={() => onRotateDocument!(-90)}
            aria-label="Rotate document counterclockwise"
          >
            <RotateCCWIcon className="size-5" />
          </Button>
          <Button
            variant="subtle"
            className="px-1"
            onClick={() => onRotateDocument!(90)}
            aria-label="Rotate document clockwise"
          >
            <RotateCWIcon className="size-5" />
          </Button>
        </ToolbarSection>
      )}

      {showDividerAfterRotate && <ToolbarSectionDivider />}

      {showAnnotationControls && (
        <ToolbarSection>
          {showSelectTool && (
            <ToggleButton
              isSelected={selectedToolName == 'select'}
              onClick={() => {
                sendAnnotationMessage({ type: 'selectTool', toolName: 'select' })
              }}
              aria-label="Select tool"
            >
              <CursorIcon className="size-6" />
            </ToggleButton>
          )}

          {showCaretTool && (
            <ToggleButton
              isSelected={selectedToolName == 'caret'}
              onClick={() => {
                sendAnnotationMessage({ type: 'selectTool', toolName: 'caret' })
              }}
              aria-label="Pointer tool"
            >
              <CursorIcon className="size-6" />
            </ToggleButton>
          )}

          <ToggleButton
            isSelected={tool?.name == 'text'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'text' })
            }}
            aria-label="Text tool"
          >
            <TextIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'checkmark'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'checkmark' })
            }}
            aria-label="Checkmark tool"
          >
            <CheckIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'stamp'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'stamp' })
            }}
            aria-label="Stamp tool"
          >
            <StampIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'cross_references'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'cross_references' })
            }}
            aria-label="Reference stamp tool"
          >
            <LinkIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'rectangle'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'rectangle' })
            }}
            aria-label="Rectangle tool"
          >
            <RectangleIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'calculator_tape'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'calculator_tape' })
            }}
            aria-label="Calculator tape tool"
          >
            <CalculatorIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'comment'}
            onClick={() => {
              sendAnnotationMessage({ type: 'selectTool', toolName: 'comment' })
            }}
            aria-label="Comment tool"
          >
            <CommentIcon className="size-6" />
          </ToggleButton>
        </ToolbarSection>
      )}

      {showAnnotationControls && <ToolbarSectionDivider />}

      {showAnnotationControls && (
        <ToolbarSection>
          {tool && (
            <SelectedTool
              tool={tool}
              activeAnnotation={activeAnnotation}
              send={sendAnnotationToolMessage}
            />
          )}
        </ToolbarSection>
      )}

      {fieldsSidebar && <div className="flex-1" />}

      {fieldsSidebar && (
        <ToolbarSection>
          <ToggleButton
            isSelected={fieldsSidebar.shown}
            onClick={fieldsSidebar.onToggle}
            aria-label="Show extracted fields"
          >
            <AIIcon className="size-6" />
          </ToggleButton>
        </ToolbarSection>
      )}
    </Toolbar>
  )
}

function SelectedTool({
  tool,
  activeAnnotation,
  send
}: {
  tool: ToolState
  activeAnnotation: STViewerAnnotation | undefined
  send: (message: ToolMessage<DocumentAnnotation, ToolState>) => void
}) {
  switch (tool.name) {
    case 'rectangle':
      return (
        <RectangleToolEditor
          toolState={tool}
          activeAnnotation={activeAnnotation?.type === 'rectangle' ? activeAnnotation : undefined}
          send={send}
        />
      )
    case 'text':
      return (
        <TextToolEditor
          toolState={tool}
          activeAnnotation={activeAnnotation?.type === 'text' ? activeAnnotation : undefined}
          send={send}
        />
      )
    case 'stamp':
      return (
        <StampToolEditor
          toolState={tool}
          send={send}
          activeAnnotation={activeAnnotation?.type === 'stamp' ? activeAnnotation : undefined}
        />
      )
    case 'checkmark':
      return (
        <CheckmarkToolEditor
          toolState={tool}
          send={send}
          activeAnnotation={activeAnnotation?.type === 'stamp' ? activeAnnotation : undefined}
        />
      )
    case 'cross_references':
      return (
        <CrossRefToolEditor
          toolState={tool}
          send={send}
          activeAnnotation={activeAnnotation?.type === 'stamp' ? activeAnnotation : undefined}
        />
      )
    case 'comment':
    case 'caret':
    case 'select':
    case 'field':
    case 'calculator_tape':
    case 'sign_offs':
      return null
  }
}

function resolveZoomLevel(currentZoomLevel: number, delta: number): number | undefined {
  const index = ST_DOC_VIEWER_ZOOM_LEVELS.indexOf(currentZoomLevel)
  return ST_DOC_VIEWER_ZOOM_LEVELS[index + delta]
}
